import { Antriebsart, FahrzeugKlasse, MVSErgebnis, MVSParams, Zahlungsintervall } from '../types/mvs'

/**
 * Klasse zur Berechnung der Motorbezogenen Versicherungssteuer in Österreich
 * gemäß § 6 Abs. 3 des Versicherungssteuergesetzes 1953.
 */

/**
 * Berechnet die Motorbezogene Versicherungssteuer gemäß dem angegebenen Zahlungsintervall.
 * Berücksichtigt automatisch die Befreiungen gemäß § 4 Abs. 3 Z 6 und 7.
 * 
 * @param params Parameter für die MVS-Berechnung
 * @returns Das Ergebnis der MVS-Berechnung
 */
export function berechneAlteSteuer(params: MVSParams): MVSErgebnis {
  const {
    fahrzeugKlasse,
    antriebsart,
    erstzulassungsDatum,
    motorLeistungKW,
    hubraumCCM,
    co2EmissionGKM,
    eigengewichtKG,
    zahlungsintervall,
    istFremdzuendungsMotorVor1987,
    haeltSchadstoffgrenzwerteEin
  } = params

  // Befreiung für Elektrofahrzeuge mit 0 CO2-Emission (§ 4 Abs. 3 Z 6)
  if (antriebsart === Antriebsart.ELEKTRISCH || (co2EmissionGKM !== undefined && co2EmissionGKM === 0)) {
    return {
      monatlicheSteuer: 0,
      steuerProIntervall: 0,
      steuerJaehrlich: 0
    }
  }
  
  // Befreiung für Krafträder mit Hubraum bis 100 ccm (§ 4 Abs. 3 Z 7)
  if (fahrzeugKlasse === FahrzeugKlasse.L1E_BIS_L5E && hubraumCCM !== undefined && hubraumCCM <= 100) {
    return {
      monatlicheSteuer: 0,
      steuerProIntervall: 0,
      steuerJaehrlich: 0
    }
  }

  // Stichtag für neue Berechnungsmethode
  const stichtag = new Date(2020, 9, 1) // Monat ist 0-basiert, daher 9 für Oktober
  const istNachStichtag = erstzulassungsDatum >= stichtag

  // Grundsteuer berechnen (monatlich)
  let monatlicheSteuer = 0

  // Berechnung je nach Fahrzeugklasse und Antriebsart
  switch (fahrzeugKlasse) {
    case FahrzeugKlasse.L1E_BIS_L5E:
      monatlicheSteuer = berechneL1EBisL5E(antriebsart, istNachStichtag, motorLeistungKW, hubraumCCM, co2EmissionGKM)
      break
    case FahrzeugKlasse.M1_BIS_35T:
      monatlicheSteuer = berechneM1Bis35T(antriebsart, istNachStichtag, motorLeistungKW, co2EmissionGKM, eigengewichtKG, erstzulassungsDatum)
      break
    case FahrzeugKlasse.WOHNMOBIL_M1_SA:
      // Prüfen, ob der Parameter aufbauSA in den Params existiert
      const aufbauSA = (params as any).aufbauSA === true
      if (aufbauSA) {
        // Wohnmobil mit SA-Aufbau: Berechnung nach Punkt c) mit Obergrenze (wie andere Fahrzeuge)
        // Dies entspricht der Checkbox "Mit Aufbau SA" im UI, wenn sie aktiviert ist
        monatlicheSteuer = berechneAndereBis35T(antriebsart, istNachStichtag, motorLeistungKW)
      } else {
        // Wohnmobil ohne SA-Aufbau: Berechnung nach Punkt b) ohne Obergrenze (wie PKW)
        // Dies entspricht der Checkbox "Mit Aufbau SA" im UI, wenn sie deaktiviert ist
        monatlicheSteuer = berechneM1Bis35T(antriebsart, istNachStichtag, motorLeistungKW, co2EmissionGKM, eigengewichtKG, erstzulassungsDatum)
      }
      break
    case FahrzeugKlasse.LKW_N1_BIS_35T:
      monatlicheSteuer = berechneAndereBis35T(antriebsart, istNachStichtag, motorLeistungKW)
      break
    case FahrzeugKlasse.ANDERE_BIS_35T:
      monatlicheSteuer = berechneAndereBis35T(antriebsart, istNachStichtag, motorLeistungKW)
      break
  }

  // Zuschlag für Zahlungsintervall (nur für bestimmte Fahrzeuge)
  if (istZuschlagFuerZahlungsintervallAnwendbar(fahrzeugKlasse, antriebsart, istNachStichtag)) {
    monatlicheSteuer = monatlicheSteuer * getZahlungsintervallFaktor(zahlungsintervall)
  }

  // Zuschlag für alte Fremdzündungsmotoren
  if (istFremdzuendungsMotorVor1987 && !haeltSchadstoffgrenzwerteEin &&
      istZuschlagFuerAlteFremdzuendungsmotorenAnwendbar(fahrzeugKlasse, antriebsart, istNachStichtag)) {
    monatlicheSteuer = monatlicheSteuer * 1.2 // 20% Zuschlag gemäß § 6 Abs. 3 Z 1 lit. b sublit. aa
  }

  // Steuer für das angegebene Zahlungsintervall berechnen
  const monate = getMonateFuerZahlungsintervall(zahlungsintervall)
  const steuerProIntervall = monatlicheSteuer * monate

  return {
    monatlicheSteuer,
    steuerProIntervall,
    steuerJaehrlich: monatlicheSteuer * 12
  }
}

/**
 * Gibt die Anzahl der Monate für das angegebene Zahlungsintervall zurück.
 */
function getMonateFuerZahlungsintervall(zahlungsintervall: Zahlungsintervall): number {
  switch (zahlungsintervall) {
    case Zahlungsintervall.JAEHRLICH:
      return 12
    case Zahlungsintervall.HALBJAEHRLICH:
      return 6
    case Zahlungsintervall.VIERTELJAEHRLICH:
      return 3
    case Zahlungsintervall.MONATLICH:
    default:
      return 1
  }
}

function berechneL1EBisL5E(antriebsart: Antriebsart, istNachStichtag: boolean, 
                          motorLeistungKW: number, hubraumCCM?: number, co2EmissionGKM?: number): number {
  if (!istNachStichtag) {
    // Vor 1. Oktober 2020: 0,025 Euro je Kubikzentimeter des Hubraums
    // Gemäß § 6 Abs. 3 Z 1 lit. a sublit. aa
    return 0.025 * (hubraumCCM || 0)
  } else {
    // Nach 30. September 2020: Kombination aus Hubraum und CO2-Emission
    // Gemäß § 6 Abs. 3 Z 1 lit. a sublit. bb
    const hubraumAnteil = 0.014 * Math.max((hubraumCCM || 0) - 52, 0)
    const co2Anteil = 0.20 * Math.max((co2EmissionGKM || 0) - 52, 10)
    return hubraumAnteil + co2Anteil
  }
}

function berechneM1Bis35T(antriebsart: Antriebsart, istNachStichtag: boolean, 
                         motorLeistungKW: number, co2EmissionGKM?: number, 
                         eigengewichtKG?: number, erstzulassungsDatum?: Date): number {
  if (!istNachStichtag) {
    // Vor 1. Oktober 2020: Gestaffelte Berechnung nach Leistung
    // Gemäß § 6 Abs. 3 Z 1 lit. b sublit. aa
    return berechneVerbrennungsLeistungM1Alt(motorLeistungKW)
  } else {
    // Nach 30. September 2020: Punkt bb) für alle Fahrzeuge mit CO2-Emissionswerten
    // Zulassungsjahr ermitteln für die jährliche Absenkung der Abzugsbeträge
    const zulassungsJahr = erstzulassungsDatum ? erstzulassungsDatum.getFullYear() : new Date().getFullYear()
    const abzugsbetragLeistung = getAbzugsbetragLeistungAlt(zulassungsJahr)
    const abzugsbetragCO2 = getAbzugsbetragCO2Alt(zulassungsJahr)
    
    // Für alle Fahrzeuge nach dem Stichtag gilt das gleiche Berechnungsschema
    // Gemäß § 6 Abs. 3 Z 1 lit. b sublit. bb
    const berechnungsLeistung = Math.max(motorLeistungKW - abzugsbetragLeistung, 5)
    const berechnungsCO2 = Math.max((co2EmissionGKM || 0) - abzugsbetragCO2, 5)
    
    return 0.72 * berechnungsLeistung + 0.72 * berechnungsCO2
  }
}

function berechneVerbrennungsLeistungM1Alt(motorLeistungKW: number): number {
  // Gemäß § 6 Abs. 3 Z 1 lit. b sublit. aa
  const berechnungsLeistung = Math.max(motorLeistungKW - 24, 0)
  const leistungAnteil1 = Math.min(berechnungsLeistung, 66) * 0.62
  const leistungAnteil2 = Math.max(Math.min(berechnungsLeistung - 66, 20), 0) * 0.66
  const leistungAnteil3 = Math.max(berechnungsLeistung - 86, 0) * 0.75
  return Math.max(leistungAnteil1 + leistungAnteil2 + leistungAnteil3, 6.20)
}

// Hinweis: Wohnmobile werden je nach Aufbau in berechneM1Bis35T bzw. berechneAndereBis35T berücksichtigt.

function berechneAndereBis35T(antriebsart: Antriebsart, istNachStichtag: boolean, motorLeistungKW: number): number {
  if (!istNachStichtag) {
    // Vor 1. Oktober 2020
    // Gemäß § 6 Abs. 3 Z 1 lit. c sublit. aa
    return berechneVerbrennungsLeistungAndereAlt(motorLeistungKW)
  } else {
    // Nach 30. September 2020
    // Gemäß § 6 Abs. 3 Z 1 lit. c sublit. bb
    return berechneVerbrennungsLeistungAndereNeu(motorLeistungKW)
  }
}

function berechneVerbrennungsLeistungAndereAlt(motorLeistungKW: number): number {
  // Gemäß § 6 Abs. 3 Z 1 lit. c sublit. aa
  const berechnungsLeistung = Math.max(motorLeistungKW - 24, 0)
  const leistungAnteil1 = Math.min(berechnungsLeistung, 66) * 0.62
  const leistungAnteil2 = Math.max(Math.min(berechnungsLeistung - 66, 20), 0) * 0.66
  const leistungAnteil3 = Math.max(berechnungsLeistung - 86, 0) * 0.75
  return Math.min(Math.max(leistungAnteil1 + leistungAnteil2 + leistungAnteil3, 6.20), 72.0)
}

function berechneVerbrennungsLeistungAndereNeu(motorLeistungKW: number): number {
  // Gemäß § 6 Abs. 3 Z 1 lit. c sublit. bb
  const berechnungsLeistung = Math.max(motorLeistungKW - 24, 0)
  const leistungAnteil1 = Math.min(berechnungsLeistung, 66) * 0.65
  const leistungAnteil2 = Math.max(Math.min(berechnungsLeistung - 66, 20), 0) * 0.70
  const leistungAnteil3 = Math.max(berechnungsLeistung - 86, 0) * 0.79
  return Math.min(Math.max(leistungAnteil1 + leistungAnteil2 + leistungAnteil3, 6.50), 76.0)
}

function getZahlungsintervallFaktor(zahlungsintervall: Zahlungsintervall): number {
  // Gemäß § 6 Abs. 3 Z 2
  switch (zahlungsintervall) {
    case Zahlungsintervall.HALBJAEHRLICH:
      return 1.06 // +6%
    case Zahlungsintervall.VIERTELJAEHRLICH:
      return 1.08 // +8%
    case Zahlungsintervall.MONATLICH:
      return 1.10 // +10%
    case Zahlungsintervall.JAEHRLICH:
    default:
      return 1.0 // kein Zuschlag
  }
}

function istZuschlagFuerZahlungsintervallAnwendbar(
  fahrzeugKlasse: FahrzeugKlasse, 
  antriebsart: Antriebsart, 
  istNachStichtag: boolean
): boolean {
  // Für alle Fahrzeuge mit Erstzulassung nach dem 30. September 2020 
  // entfallen die Zuschläge für unterjährige Zahlungen komplett
  if (istNachStichtag) {
    return false
  }
  
  // Bei Fahrzeugen vor dem Stichtag:
  // Zuschlag für Zahlungsintervall gilt nicht für Elektrofahrzeuge
  if (antriebsart === Antriebsart.ELEKTRISCH) {
    return false
  }
  
  return true
}

function istZuschlagFuerAlteFremdzuendungsmotorenAnwendbar(
  fahrzeugKlasse: FahrzeugKlasse, 
  antriebsart: Antriebsart, 
  istNachStichtag: boolean
): boolean {
  // Zuschlag für alte Fremdzündungsmotoren gilt nicht für:
  // 1. Elektrofahrzeuge
  if (antriebsart === Antriebsart.ELEKTRISCH) {
    return false
  }
  
  // 2. Hybridfahrzeuge nach Stichtag
  if (istNachStichtag && 
     (antriebsart === Antriebsart.HYBRID_EXTERN_AUFLADBAR || 
      antriebsart === Antriebsart.HYBRID_NICHT_EXTERN_AUFLADBAR)) {
    return false
  }
  
  return true
}

/**
 * Berechnet den Abzugsbetrag für die Leistung basierend auf dem Zulassungsjahr.
 * Gemäß Punkt 9. a) wird beginnend mit 1. Jänner 2021 jährlich der Wert 65 Kilowatt 
 * um den Wert 1 abgesenkt.
 * 
 * @param jahr Zulassungsjahr des Fahrzeugs
 * @returns Abzugsbetrag für die Leistung in Kilowatt
 */
function getAbzugsbetragLeistungAlt(jahr: number): number {
  // Beginnend mit 1. Jänner 2021, jährliche Absenkung um 1
  if (jahr >= 2021) {
    // 65 - (1 × Jahre seit 2021)
    return Math.max(65 - (jahr - 2020), 0)
  }
  return 65 // Ursprungswert für 2020
}

/**
 * Berechnet den Abzugsbetrag für CO2-Emissionen basierend auf dem Zulassungsjahr.
 * Gemäß Punkt 9. a) wird beginnend mit 1. Jänner 2021 jährlich der Wert 115 Gramm pro Kilometer
 * um den Wert 3 abgesenkt.
 * 
 * @param jahr Zulassungsjahr des Fahrzeugs
 * @returns Abzugsbetrag für CO2-Emissionen in g/km
 */
function getAbzugsbetragCO2Alt(jahr: number): number {
  // Beginnend mit 1. Jänner 2021, jährliche Absenkung um 3
  if (jahr >= 2021) {
    // 115 - (3 × Jahre seit 2021)
    return Math.max(115 - ((jahr - 2020) * 3), 0)
  }
  return 115 // Ursprungswert für 2020
}
